// src/components/ProductPage/ProductDetails.js
import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Divider, 
  Chip,
  Alert 
} from '@mui/material';
import { ShoppingBag, LocalOffer } from '@mui/icons-material';
import RichTextDisplay from '../Common/RichTextDisplay';
import ColorSelector from './ColorSelector';
import SizeSelector from './SizeSelector';
import QuantitySelector from './QuantitySelector';

const ProductDetails = ({
  product,
  productColors,
  selectedColorIndex,
  handleColorChange,
  availableSizes,
  selectedSize,
  handleSizeChange,
  quantity,
  setQuantity,
  handleAddToCart,
  error,
  displayPrice,
  originalPrice,
  hasDiscount,
  discountPercent
}) => {
  return (
    <Box sx={{ pl: { md: 2 } }}>
      {/* Category and Discount Badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        {/* Discount Badge */}
        {hasDiscount && discountPercent > 0 && (
          <Chip
            icon={<LocalOffer />}
            label={`خصم %${discountPercent}`}
            color="error"
            size="small"
            sx={{
              fontWeight: 'bold',
              fontSize: '0.8rem',
              px: 1.5
            }}
          />
        )}

        {/* Category */}
        {product.categories && (
          <Chip
            label={product.categories.name}
            size="small"
            sx={{
              bgcolor: '#f0f8ff',
              color: '#0f3662',
              fontWeight: 'bold'
            }}
          />
        )}
      </Box>

      {/* Product Name */}
      <Typography 
        variant="h4" 
        sx={{ 
          fontWeight: 'bold',
          mb: 2,
          color: '#333',
          fontSize: { xs: '1.5rem', md: '2rem' }
        }}
      >
        {product.name_ar}
      </Typography>
      
      {/* Price Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {/* Current Price */}
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 'bold',
            color: hasDiscount ? '#e74c3c' : '#0f3662',
            fontSize: { xs: '1.5rem', md: '2rem' }
          }}
        >
          {displayPrice.toFixed(2)} ج.م
        </Typography>

        {/* Original Price (crossed out) */}
        {hasDiscount && originalPrice && (
          <Typography
            variant="h6"
            sx={{
              textDecoration: 'line-through',
              color: '#999',
              fontSize: { xs: '1rem', md: '1.25rem' }
            }}
          >
            {originalPrice.toFixed(2)} ج.م
          </Typography>
        )}
      </Box>

      {/* Savings Amount */}
      {hasDiscount && originalPrice && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: '#27ae60',
              fontWeight: 'bold',
              bgcolor: '#e8f5e8',
              padding: '8px 12px',
              borderRadius: 1,
              display: 'inline-block'
            }}
          >
            توفير {(originalPrice - displayPrice).toFixed(2)} ج.م
          </Typography>
        </Box>
      )}
      
      {/* Description */}
      <RichTextDisplay 
        content={product.description_ar}
        sx={{ mb: 4 }}
      />

      <Divider sx={{ mb: 4 }} />

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Color Selection */}
      {productColors.length > 0 && (
        <ColorSelector 
          productColors={productColors}
          selectedColorIndex={selectedColorIndex}
          handleColorChange={handleColorChange}
        />
      )}

      {/* Size Selection */}
      {availableSizes.length > 0 && (
        <SizeSelector 
          availableSizes={availableSizes}
          selectedSize={selectedSize}
          handleSizeChange={handleSizeChange}
        />
      )}

      {/* Quantity Selection */}
      {selectedSize && (
        <QuantitySelector 
          quantity={quantity}
          setQuantity={setQuantity}
          maxQuantity={availableSizes.find(s => s.size === selectedSize)?.stock || 0}
        />
      )}

      {/* Discount Info */}
      {hasDiscount && (
        <Box sx={{ 
          mt: 3, 
          mb: 3,
          p: 2,
          bgcolor: '#fff3cd', 
          borderRadius: 2,
          border: '1px solid #ffeaa7'
        }}>
          <Typography variant="body2" sx={{ color: '#856404', fontWeight: 'bold' }}>
            🔥 عرض خاص - خصم {discountPercent}%
          </Typography>
          <Typography variant="body2" sx={{ color: '#856404', mt: 0.5 }}>
            العرض محدود، اطلب الآن!
          </Typography>
        </Box>
      )}

      {/* Add to Cart Button */}
      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={handleAddToCart}
        disabled={selectedSize && availableSizes.find(s => s.size === selectedSize)?.stock < 1}
        startIcon={<ShoppingBag />}
        sx={{
          py: 2,
          fontSize: '1.1rem',
          fontWeight: 'bold',
          backgroundColor: '#0f3662',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(15, 54, 98, 0.3)',
          flexDirection: 'row-reverse',
          '&:hover': { 
            backgroundColor: '#0a2a4a',
            boxShadow: '0 6px 16px rgba(15, 54, 98, 0.4)'
          },
          '&:disabled': { 
            backgroundColor: '#ccc',
            boxShadow: 'none'
          }
        }}
      >
        {selectedSize && availableSizes.find(s => s.size === selectedSize)?.stock < 1
          ? 'غير متوفر' 
          : 'أضف إلى السلة'}
      </Button>

      {/* Stock Info */}
      {selectedSize && (
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center',
            mt: 2,
            color: '#666'
          }}
        >
          متوفر: {availableSizes.find(s => s.size === selectedSize)?.stock || 0} قطعة
        </Typography>
      )}
    </Box>
  );
};

export default ProductDetails;
